import React from 'react';
import {
  Text,
  Select,
  Group,
  Table,
  ActionIcon,
  Tooltip,
  Pill,
  Center,
  Badge,
} from '@mantine/core';
import { IconWand } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { User } from '../../types';
import {
  PopulatedParticipant,
  getParticipantUserId,
  getParticipantName,
} from './assignmentsMatrix.helpers';

interface AssignmentsTableViewProps {
  participants: PopulatedParticipant[];
  usersArr: User[];
  pairs: Record<string, string[]>;
  userNameById: Record<string, string>;
  onAddReviewer: (subjectId: string, reviewerId: string) => void;
  onRemoveReviewer: (subjectId: string, reviewerId: string) => void;
  onAutoFill: (subjectId: string) => void;
}

export const AssignmentsTableView: React.FC<AssignmentsTableViewProps> = ({
  participants,
  usersArr,
  pairs,
  userNameById,
  onAddReviewer,
  onRemoveReviewer,
  onAutoFill,
}) => {
  const { t } = useTranslation();
  return (
    <Table.ScrollContainer minWidth={900}>
      <Table verticalSpacing="md" withRowBorders highlightOnHover style={{ tableLayout: 'fixed' }}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={{ width: 200 }}>{t('perfReview.colParticipant')}</Table.Th>
            <Table.Th style={{ width: '30%' }}>{t('perfReview.colWishedHdr')}</Table.Th>
            <Table.Th>{t('perfReview.colAssignedHdr')}</Table.Th>
            <Table.Th style={{ width: 220 }}>{t('perfReview.colAddHdr')}</Table.Th>
            <Table.Th style={{ width: 60 }}></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {(participants || []).map((p) => {
            const uid = getParticipantUserId(p);
            const name = getParticipantName(p) || uid;
            const wished = p.requestedReviewers || [];
            const assigned = pairs[uid] || [];
            const otherOptions = usersArr
              .filter((u) => u._id !== uid && assigned.indexOf(u._id) === -1)
              .map((u) => ({ value: u._id, label: u.name }));
            return (
              <Table.Tr key={p._id}>
                <Table.Td>
                  <Text fw={500}>{name}</Text>
                </Table.Td>
                <Table.Td>
                  {wished.length ? (
                    <Group gap={4} wrap="wrap">
                      {wished.map((rid) => {
                        const isAssigned = assigned.indexOf(rid) !== -1;
                        return (
                          <Badge
                            key={rid}
                            color={isAssigned ? 'teal' : 'gray'}
                            variant={isAssigned ? 'light' : 'outline'}
                            style={{ cursor: isAssigned ? 'default' : 'pointer' }}
                            onClick={() => {
                              if (!isAssigned) onAddReviewer(uid, rid);
                            }}
                          >
                            {isAssigned ? '✓ ' : '+ '}
                            {userNameById[rid] || rid.slice(0, 6)}
                          </Badge>
                        );
                      })}
                    </Group>
                  ) : (
                    <Text size="sm" c="dimmed">
                      —
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  {assigned.length ? (
                    <Group gap={4} wrap="wrap">
                      {assigned.map((rid) => (
                        <Pill
                          key={rid}
                          withRemoveButton
                          onRemove={() => onRemoveReviewer(uid, rid)}
                        >
                          {userNameById[rid] || rid.slice(0, 6)}
                        </Pill>
                      ))}
                    </Group>
                  ) : (
                    <Text size="sm" c="dimmed">
                      {t('perfReview.noneAssigned')}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Select
                    placeholder={t('perfReview.search')}
                    data={otherOptions}
                    value={null}
                    onChange={(v) => {
                      if (v) onAddReviewer(uid, v);
                    }}
                    searchable
                    clearable={false}
                    size="xs"
                    nothingFoundMessage={t('perfReview.nothingFound')}
                  />
                </Table.Td>
                <Table.Td>
                  <Tooltip label={t('perfReview.autoFillTooltip')}>
                    <ActionIcon variant="light" onClick={() => onAutoFill(uid)}>
                      <IconWand size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Table.Td>
              </Table.Tr>
            );
          })}
          {!participants?.length && (
            <Table.Tr>
              <Table.Td colSpan={5}>
                <Center py="lg">
                  <Text c="dimmed">{t('perfReview.emptyAssignmentsHint')}</Text>
                </Center>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
};
